import React from 'react';
import { FaUsers, FaAward, FaShieldAlt, FaBullseye, FaHeart, FaRocket } from 'react-icons/fa';
import SEO from '../components/SEO';

const About: React.FC = () => {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    mainEntity: {
      '@type': 'Organization',
      name: 'JackpotQuantum.com',
      description: 'Leading UK casino review platform providing expert analysis, exclusive bonuses, and trusted recommendations.',
    },
  };

  const values = [
    {
      icon: FaShieldAlt,
      title: 'Trust & Transparency',
      description: 'We only recommend casinos licensed by the UK Gambling Commission. Every review is honest, unbiased, and based on real player experiences.',
    },
    {
      icon: FaAward,
      title: 'Expert Analysis',
      description: 'Our team of gambling experts tests every casino thoroughly, evaluating games, bonuses, security, and customer support to provide accurate ratings.',
    },
    {
      icon: FaUsers,
      title: 'Player-First Approach',
      description: 'Your safety and satisfaction are our top priorities. We help you make informed decisions and find casinos that truly value their players.',
    },
    {
      icon: FaBullseye,
      title: 'Comprehensive Reviews',
      description: 'We cover everything from welcome bonuses to withdrawal speeds, game selection to mobile compatibility, ensuring you have all the information you need.',
    },
    {
      icon: FaHeart,
      title: 'Responsible Gambling',
      description: 'We promote responsible gambling and provide resources for players who need support. Your wellbeing matters more than any bonus.',
    },
    {
      icon: FaRocket,
      title: 'Always Evolving',
      description: 'The casino industry changes constantly. We stay updated with the latest trends, new casinos, and updated offers to keep you ahead of the game.',
    },
  ];

  return (
    <>
      <SEO
        title="About Us - Your Trusted UK Casino Review Platform"
        description="Learn about JackpotQuantum.com - your trusted source for comprehensive UK casino reviews, expert analysis, and exclusive bonus offers."
        canonical="/about"
        jsonLd={jsonLd}
      />

      <div className="min-h-screen bg-dark-900">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-dark-800 to-dark-900">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                <span className="text-gradient">About</span> <span className="text-white">JackpotQuantum</span>
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed">
                We're dedicated to helping UK players find the best casino experiences. Our mission is to provide honest, 
                comprehensive reviews that empower you to make informed decisions and enjoy safe, responsible gambling.
              </p>
            </div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-20 bg-dark-800">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl font-bold text-white mb-8 text-center">
                <span className="text-gradient">Our Mission</span>
              </h2>
              <div className="glass rounded-2xl p-8 md:p-12">
                <p className="text-gray-300 text-lg leading-relaxed mb-6">
                  At JackpotQuantum.com, we believe that finding the right casino should be simple, transparent, and trustworthy. 
                  The UK online casino market is vast, with hundreds of operators competing for your attention. Our goal is to cut 
                  through the noise and provide you with accurate, unbiased information.
                </p>
                <p className="text-gray-300 text-lg leading-relaxed mb-6">
                  We test every casino we review. Our team signs up, makes deposits, plays games, tests withdrawals, and evaluates 
                  customer support. We don't just read terms and conditions – we experience them firsthand. This hands-on approach 
                  ensures our reviews reflect real player experiences, not marketing promises.
                </p>
                <p className="text-gray-300 text-lg leading-relaxed">
                  Whether you're a seasoned player looking for the best VIP programs or a newcomer seeking your first welcome bonus, 
                  we're here to guide you. We understand that every player is different, which is why we provide detailed breakdowns 
                  covering all aspects of each casino.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-20 bg-dark-900">
          <div className="container mx-auto px-4">
            <h2 className="text-4xl font-bold text-white mb-12 text-center">
              <span className="text-gradient">Our Values</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {values.map((value, i) => {
                const Icon = value.icon;
                return (
                  <div key={i} className="glass rounded-xl p-6 card-hover">
                    <div className="w-16 h-16 bg-gradient-to-br from-quantum-400 to-quantum-600 rounded-full flex items-center justify-center mb-4">
                      <Icon className="w-8 h-8 text-dark-900" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3">{value.title}</h3>
                    <p className="text-gray-400">{value.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* How We Review Section */}
        <section className="py-20 bg-dark-800">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl font-bold text-white mb-8 text-center">
                <span className="text-gradient">How We Review Casinos</span>
              </h2>
              <div className="space-y-6">
                {[
                  {
                    step: '01',
                    title: 'Registration & First Impressions',
                    description: 'We start by creating an account and evaluating the sign-up process, website design, and overall user experience. First impressions matter, and we note everything from loading speeds to navigation ease.',
                  },
                  {
                    step: '02',
                    title: 'Bonus Analysis',
                    description: 'We claim welcome bonuses and thoroughly analyze terms and conditions. We calculate real wagering requirements, test bonus eligibility, and document any restrictions or limitations.',
                  },
                  {
                    step: '03',
                    title: 'Game Testing',
                    description: 'We play a variety of games across different categories – slots, table games, live casino, and jackpots. We evaluate game quality, provider diversity, RTP rates, and mobile compatibility.',
                  },
                  {
                    step: '04',
                    title: 'Payment Processing',
                    description: 'We test deposits and withdrawals using multiple payment methods. We document processing times, fees, limits, and any issues encountered during transactions.',
                  },
                  {
                    step: '05',
                    title: 'Customer Support Evaluation',
                    description: 'We contact support through live chat, email, and phone (when available) to test response times, knowledge, and helpfulness. We ask real questions and document the quality of service.',
                  },
                  {
                    step: '06',
                    title: 'Security & Licensing Verification',
                    description: 'We verify licensing information, check SSL certificates, review privacy policies, and ensure responsible gambling tools are available and functional.',
                  },
                ].map((item, i) => (
                  <div key={i} className="glass rounded-xl p-6 md:p-8">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-quantum-400 to-quantum-600 rounded-lg flex items-center justify-center">
                        <span className="text-2xl font-bold text-dark-900">{item.step}</span>
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-2xl font-bold text-white mb-3">{item.title}</h3>
                        <p className="text-gray-300 leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-quantum-900/20 to-dark-900">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl font-bold text-white mb-4">
              Ready to Find Your Perfect Casino?
            </h2>
            <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
              Explore our comprehensive reviews and discover casinos that match your preferences. 
              Start with our top-rated offers or compare bonuses to find the best value.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="/#offers"
                className="bg-gradient-to-r from-quantum-500 to-quantum-600 text-white py-4 px-8 rounded-xl font-bold text-lg hover:from-quantum-600 hover:to-quantum-700 transition-all transform hover:scale-105 shadow-lg shadow-quantum-500/30"
              >
                View Top Offers
              </a>
              <a
                href="/comparison"
                className="glass text-white py-4 px-8 rounded-xl font-bold text-lg hover:bg-dark-700 transition-all"
              >
                Compare Casinos
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default About;

